import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "./AuthContext";

/**
 * The parameter that names the learner a URL belongs to. Must match
 * `Auth::STUDENT_PARAM` on the server.
 */
export const STUDENT_PARAM = "as";

/**
 * Keeps `?as=<learner id>` on every URL while a learner's session is open, so
 * whatever is in the address bar can be copied, bookmarked or sent on and it
 * will open that learner's account again.
 *
 * The id authorises nothing by itself — the server only acts on it for a caller
 * already signed in as a guardian, and drops it for anyone else. That is why it
 * can safely ride along everywhere rather than being handed out once.
 *
 * Rewriting through the router (rather than history.replaceState) keeps React
 * Router's own idea of the location in step with the address bar.
 */
export function useStickyStudentParam() {
  const { role, student } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Grown-up URLs stay clean: a guardian is not browsing "as" anyone.
    if (role !== "student" || !student?.id) return;

    // Read the address bar rather than this render's `location`, and that is the
    // whole of the fix rather than a detail.
    //
    // This hook lives in `ShellRouter`, above the route tree, so its effect runs
    // AFTER the effects of everything inside it — React flushes children first.
    // `KidRoutes` ends in `<Route path="*" element={<Navigate to="/" replace />} />`,
    // whose whole job is to put a learner who opened some other URL back on their
    // own home page. That `<Navigate>` fires first and replaces the entry with
    // `/`; then this effect ran, still holding the pathname from the render
    // before it, and replaced the entry AGAIN — resurrecting the very path the
    // redirect had just left. Both are `replace`, so the second one won.
    //
    // What the child then saw was a blank white page, for good: the location was
    // back on an unmatched path, `<Navigate>` had already mounted so its effect
    // would not fire a second time, and a `<Navigate>` renders nothing. Every
    // stale bookmark, shared grown-up link and mistyped URL ended there, with no
    // way out but editing the address bar. Measured on `/students`, `/settings`
    // and `/nonexistent-path` alike.
    //
    // `navigate` writes to history synchronously, so by the time this effect
    // runs `window.location` is already the redirect's destination. Taking the
    // path from there means this hook only ever adds the parameter to wherever
    // the app has actually arrived, and can no longer undo another navigation.
    const { pathname, search, hash } = window.location;

    const params = new URLSearchParams(search);
    if (params.get(STUDENT_PARAM) === student.id) return;

    params.set(STUDENT_PARAM, student.id);
    navigate({ pathname, search: `?${params.toString()}`, hash }, { replace: true });
  }, [role, student?.id, location.pathname, location.search, location.hash, navigate]);
}
